import { useEffect, useRef } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './LandingPage.css';

/* ============================================================================
 * Tiny inline SVG icon primitives — keeps the landing bundle dependency-free
 * and matches the reference design stroke weights exactly.
 * ==========================================================================*/

const Icon = ({ d, size = 16, strokeWidth = 2, children, ...rest }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...rest}
  >
    {children ?? <path d={d} />}
  </svg>
);

const LinkIcon = (props) => (
  <Icon {...props}>
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </Icon>
);

const DashboardIcon = (props) => (
  <Icon {...props}>
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M9 9h6v6H9z" />
  </Icon>
);

const ChartIcon = (props) => (
  <Icon {...props}>
    <path d="M3 3v18h18" />
    <path d="m7 14 4-4 4 4 5-5" />
  </Icon>
);

const DomainsIcon = (props) => (
  <Icon {...props}>
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M3 9h18" />
  </Icon>
);

const QRIcon = (props) => (
  <Icon {...props}>
    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4" />
  </Icon>
);

const ABIcon = (props) => (
  <Icon {...props}>
    <circle cx="12" cy="12" r="3" />
    <path d="M12 1v6M12 17v6" />
  </Icon>
);

const CircleIcon = (props) => (
  <Icon {...props}>
    <circle cx="12" cy="12" r="10" />
    <path d="m4.93 4.93 14.14 14.14" />
  </Icon>
);

const SparkleIcon = (props) => (
  <Icon {...props}>
    <path d="M12 2L9 9l-7 1 5 5-1.5 7L12 19l5.5 3L16 15l5-5-7-1z" />
  </Icon>
);

const TargetIcon = (props) => (
  <Icon {...props}>
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="12" r="4" />
  </Icon>
);

const QRBrandedIcon = (props) => (
  <Icon {...props}>
    <rect x="3" y="3" width="7" height="7" />
    <rect x="14" y="3" width="7" height="7" />
    <rect x="3" y="14" width="7" height="7" />
    <path d="M14 14h2v2h-2zM18 14h2v2h-2zM14 18h2v2h-2zM18 18h2v2h-2z" />
  </Icon>
);

const BoltIcon = (props) => (
  <Icon {...props}>
    <path d="M13 2 3 14h9l-1 8 10-12h-9z" />
  </Icon>
);

const ShieldIcon = (props) => (
  <Icon {...props}>
    <path d="M12 2 4 6v6c0 5 3.5 9.5 8 10 4.5-.5 8-5 8-10V6z" />
    <path d="M9 12l2 2 4-4" />
  </Icon>
);

const PlayIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M8 5v14l11-7z" />
  </svg>
);

/* ============================================================================
 * Logo mark (charcoal tile with coral link glyph)
 * ==========================================================================*/

const LogoMark = () => (
  <span className="lf-logo-mark" aria-hidden>
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ff5436" strokeWidth="2.5" strokeLinecap="round">
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  </span>
);

/* ============================================================================
 * Animated counter hook — ticks a numeric ref up to `target` using eased easing.
 * ==========================================================================*/

function useCounter(ref, target, { duration = 2200, delay = 0, suffix = '', decimals = 0 } = {}) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      el.textContent =
        decimals > 0
          ? target.toFixed(decimals) + suffix
          : Math.floor(target).toLocaleString() + suffix;
      return undefined;
    }

    let raf;
    let startTs;
    const timer = window.setTimeout(() => {
      const tick = (now) => {
        if (!startTs) startTs = now;
        const progress = Math.min((now - startTs) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        const current = target * eased;
        el.textContent =
          decimals > 0
            ? current.toFixed(decimals) + suffix
            : Math.floor(current).toLocaleString() + suffix;
        if (progress < 1) raf = window.requestAnimationFrame(tick);
      };
      raf = window.requestAnimationFrame(tick);
    }, delay);

    return () => {
      window.clearTimeout(timer);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [ref, target, duration, delay, suffix, decimals]);
}

/* ============================================================================
 * LandingPage — route "/"
 * ==========================================================================*/

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const bigCounterRef = useRef(null);
  const counter2Ref = useRef(null);
  const counter3Ref = useRef(null);
  const rowRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];
  const miniBarRefs = useRef([]);
  miniBarRefs.current = [];
  const registerBar = (el) => {
    if (el && !miniBarRefs.current.includes(el)) miniBarRefs.current.push(el);
  };

  const handleGetStarted = () => {
    navigate(user ? '/dashboard' : '/login');
  };

  useCounter(bigCounterRef, 12847, { duration: 2200, delay: 1000 });
  useCounter(counter2Ref, 247, { duration: 1800, delay: 1000 });
  useCounter(counter3Ref, 8.4, { duration: 1800, delay: 1000, suffix: '%', decimals: 1 });

  const rowTargets = [4287, 2841, 1923, 1247];
  useCounter(rowRefs[0], rowTargets[0], { duration: 1500, delay: 1700 });
  useCounter(rowRefs[1], rowTargets[1], { duration: 1500, delay: 1850 });
  useCounter(rowRefs[2], rowTargets[2], { duration: 1500, delay: 2000 });
  useCounter(rowRefs[3], rowTargets[3], { duration: 1500, delay: 2150 });

  // Live click simulator + mini-bars wiggle
  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return undefined;

    let liveCount = 12847;

    const liveTimer = window.setInterval(() => {
      const inc = Math.floor(Math.random() * 4) + 1;
      liveCount += inc;
      const el = bigCounterRef.current;
      if (el) {
        el.textContent = liveCount.toLocaleString();
        el.style.color = '#ff5436';
        window.setTimeout(() => {
          if (el) el.style.color = '';
        }, 400);
      }
    }, 3500);

    const wiggleTimer = window.setInterval(() => {
      const bars = miniBarRefs.current;
      if (!bars.length) return;
      const i = Math.floor(Math.random() * bars.length);
      const newH = Math.floor(30 + Math.random() * 65);
      if (bars[i]) bars[i].style.height = newH + '%';
    }, 1800);

    return () => {
      window.clearInterval(liveTimer);
      window.clearInterval(wiggleTimer);
    };
  }, []);

  return (
    <div className="lf-root">
      <div className="lf-paper" aria-hidden />

      {/* ============================  NAV  ============================ */}
      <nav className="lf-nav" aria-label="Primary">
        <RouterLink to="/" className="lf-logo">
          <LogoMark />
          LinkFlow
        </RouterLink>

        <div className="lf-nav-links">
          <a href="#features">Features</a>
          <a href="#how">How it works</a>
          <a href="#pricing">Pricing</a>
          <RouterLink to="/docs">Docs</RouterLink>
          <a href="#changelog">Changelog</a>
        </div>

        <div className="lf-nav-cta">
          {!user && (
            <RouterLink to="/login" className="lf-btn lf-btn-ghost">
              Sign in
            </RouterLink>
          )}
          <button type="button" className="lf-btn lf-btn-dark" onClick={handleGetStarted}>
            {user ? 'Open dashboard' : 'Start free'} <span className="lf-arrow">→</span>
          </button>
        </div>
      </nav>

      {/* ============================  HERO  ============================ */}
      <section className="lf-hero">
        <div className="lf-hero-bg" aria-hidden />
        <div className="lf-hero-content">
          <div className="lf-badge">
            <span className="lf-badge-dot" />
            LinkFlow 2.0 — now with AI slug suggestions
          </div>

          <h1 className="lf-headline">
            Short links your<br />
            marketing team will<br />
            <em>actually</em> use.
          </h1>

          <p className="lf-hero-sub">
            Smart routing, real-time analytics, A/B tests, retargeting pixels and AI-curated slugs — all on lfo.to. Built for teams who treat every click like revenue.
          </p>

          <div className="lf-hero-cta">
            <button type="button" className="lf-btn lf-btn-coral" onClick={handleGetStarted}>
              {user ? 'Open dashboard' : 'Start free'} <span className="lf-arrow">→</span>
            </button>
            <a href="#features" className="lf-btn lf-btn-outline">
              <PlayIcon />
              Watch 90s demo
            </a>
          </div>

          <div className="lf-hero-meta">
            No credit card · 5,000 free clicks/mo · Cancel anytime
          </div>
        </div>

        {/* ==========================  ANIMATED DASHBOARD  ========================== */}
        <div className="lf-dashboard-stage">
          {/* AI suggestion popup */}
          <div className="lf-ai-pop">
            <div className="lf-ai-pop-label">Claude suggests</div>
            <div className="lf-ai-pop-slugs">
              <div className="lf-ai-pop-slug">lfo.to/spring-launch</div>
              <div className="lf-ai-pop-slug lf-featured">lfo.to/spring-26</div>
              <div className="lf-ai-pop-slug">lfo.to/q2-launch</div>
            </div>
          </div>

          {/* Live click notification */}
          <div className="lf-notif">
            <div className="lf-notif-icon">
              <CircleIcon size={16} strokeWidth={2.5} />
            </div>
            <div className="lf-notif-text">
              <strong>+1 click · Helmond, NL</strong>
              <span>lfo.to/spring-26 · 0.3s ago</span>
            </div>
          </div>

          <div className="lf-dashboard">
            <div className="lf-dash-bar">
              <div className="lf-dash-dots">
                <span /><span /><span />
              </div>
              <div className="lf-dash-url">app.linkflow.io/dashboard</div>
            </div>

            <div className="lf-dash-body">
              <aside className="lf-dash-sidebar">
                <div className="lf-sidebar-section">
                  <div className="lf-sidebar-label">Workspace</div>
                  <div className="lf-sidebar-item active">
                    <DashboardIcon size={14} className="lf-sidebar-icon" />
                    Dashboard
                  </div>
                  <div className="lf-sidebar-item">
                    <LinkIcon size={14} className="lf-sidebar-icon" />
                    My links
                  </div>
                  <div className="lf-sidebar-item">
                    <ChartIcon size={14} className="lf-sidebar-icon" />
                    Analytics
                  </div>
                  <div className="lf-sidebar-item">
                    <DomainsIcon size={14} className="lf-sidebar-icon" />
                    Domains
                  </div>
                </div>
                <div className="lf-sidebar-section">
                  <div className="lf-sidebar-label">Tools</div>
                  <div className="lf-sidebar-item">
                    <QRIcon size={14} className="lf-sidebar-icon" />
                    QR codes
                  </div>
                  <div className="lf-sidebar-item">
                    <ABIcon size={14} className="lf-sidebar-icon" />
                    A/B tests
                  </div>
                </div>
              </aside>

              <main className="lf-dash-main">
                <div className="lf-dash-title-row">
                  <div>
                    <div className="lf-dash-title">Dashboard overview</div>
                    <div className="lf-dash-subtitle">Last 24 hours · all workspaces</div>
                  </div>
                  <div className="lf-live-badge">Live</div>
                </div>

                <div className="lf-counter-row">
                  <div className="lf-counter-card lf-primary">
                    <div className="lf-counter-label">Total clicks today</div>
                    <div className="lf-counter-value" ref={bigCounterRef}>0</div>
                    <div className="lf-counter-delta">↑ 18.4% vs yesterday</div>
                  </div>
                  <div className="lf-counter-card lf-secondary">
                    <div className="lf-counter-label">Active links</div>
                    <div className="lf-counter-value" ref={counter2Ref}>0</div>
                    <div className="lf-counter-delta">↑ 12 new</div>
                  </div>
                  <div className="lf-counter-card lf-secondary">
                    <div className="lf-counter-label">Conv. rate</div>
                    <div className="lf-counter-value" ref={counter3Ref}>0%</div>
                    <div className="lf-counter-delta">↑ 2.1pp</div>
                  </div>
                </div>

                <div className="lf-chart-area">
                  <div className="lf-chart-header">
                    <div className="lf-chart-title">Clicks over time</div>
                    <div className="lf-chart-tabs">
                      <div className="lf-chart-tab">1h</div>
                      <div className="lf-chart-tab active">24h</div>
                      <div className="lf-chart-tab">7d</div>
                      <div className="lf-chart-tab">30d</div>
                    </div>
                  </div>
                  <svg className="lf-chart-svg" viewBox="0 0 600 140" preserveAspectRatio="none" aria-hidden>
                    <defs>
                      <linearGradient id="lfChartGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#ff5436" stopOpacity="0.25" />
                        <stop offset="100%" stopColor="#ff5436" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    <line x1="0" y1="35" x2="600" y2="35" stroke="rgba(13,13,15,0.05)" />
                    <line x1="0" y1="70" x2="600" y2="70" stroke="rgba(13,13,15,0.05)" />
                    <line x1="0" y1="105" x2="600" y2="105" stroke="rgba(13,13,15,0.05)" />
                    <path
                      className="lf-chart-area-fill"
                      d="M0,110 L40,95 L80,100 L120,82 L160,88 L200,72 L240,78 L280,55 L320,62 L360,40 L400,48 L440,28 L480,35 L520,20 L560,28 L600,15 L600,140 L0,140 Z"
                      fill="url(#lfChartGrad)"
                    />
                    <path
                      className="lf-chart-path"
                      d="M0,110 L40,95 L80,100 L120,82 L160,88 L200,72 L240,78 L280,55 L320,62 L360,40 L400,48 L440,28 L480,35 L520,20 L560,28 L600,15"
                      fill="none"
                      stroke="#ff5436"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <circle cx="600" cy="15" r="5" fill="#ff5436" className="lf-chart-area-fill" />
                    <circle cx="600" cy="15" r="10" fill="#ff5436" opacity="0.2" className="lf-chart-area-fill">
                      <animate attributeName="r" values="6;14;6" dur="2s" repeatCount="indefinite" />
                      <animate attributeName="opacity" values="0.4;0;0.4" dur="2s" repeatCount="indefinite" />
                    </circle>
                  </svg>
                </div>

                <div className="lf-links-table">
                  <div className="lf-table-head">
                    <div>Link</div>
                    <div>Clicks</div>
                    <div>Tag</div>
                    <div />
                  </div>
                  {[
                    { slug: 'lfo.to/spring-26', favicon: 'S', faviconClass: '', tag: 'campaign', tagClass: '', sparkColor: '#ff5436', spark: '0,18 10,15 20,16 30,8 40,10 50,4 60,6', ref: rowRefs[0] },
                    { slug: 'lfo.to/webinar-may', favicon: 'W', faviconClass: 'lf-olive', tag: 'event', tagClass: 'lf-olive', sparkColor: '#8b9b3f', spark: '0,12 10,14 20,8 30,10 40,6 50,9 60,4', ref: rowRefs[1] },
                    { slug: 'lfo.to/yt-promo', favicon: 'Y', faviconClass: 'lf-dark', tag: 'video', tagClass: 'lf-gray', sparkColor: '#6b6b72', spark: '0,8 10,12 20,10 30,14 40,8 50,11 60,7', ref: rowRefs[2] },
                    { slug: 'lfo.to/blog-pricing', favicon: 'B', faviconClass: '', tag: 'organic', tagClass: '', sparkColor: '#ff5436', spark: '0,16 10,12 20,14 30,9 40,11 50,7 60,5', ref: rowRefs[3] },
                  ].map((row) => (
                    <div className="lf-table-row" key={row.slug}>
                      <div className="lf-row-link">
                        <div className={`lf-row-favicon ${row.faviconClass}`}>{row.favicon}</div>
                        <div>
                          <div className="lf-row-slug">{row.slug}</div>
                        </div>
                      </div>
                      <div className="lf-row-clicks" ref={row.ref}>0</div>
                      <div>
                        <span className={`lf-row-tag ${row.tagClass}`}>{row.tag}</span>
                      </div>
                      <svg className="lf-row-spark" viewBox="0 0 60 24" preserveAspectRatio="none" aria-hidden>
                        <polyline
                          points={row.spark}
                          fill="none"
                          stroke={row.sparkColor}
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                      </svg>
                    </div>
                  ))}
                </div>
              </main>
            </div>
          </div>
        </div>
      </section>

      {/* ============================  LOGOS  ============================ */}
      <div className="lf-logos">
        <div className="lf-logos-label">Trusted by growth teams everywhere</div>
        <div className="lf-logos-row">
          <span>Northwave</span>
          <span>Daksha</span>
          <span>Cohere</span>
          <span>Helix</span>
          <span>Forma</span>
          <span>Quanta</span>
        </div>
      </div>

      {/* ============================  BENTO  ============================ */}
      <section className="lf-block" id="features">
        <div className="lf-section-label">Capabilities</div>
        <h2 className="lf-section-title">
          Everything you need to <em>own</em> your links.
        </h2>
        <p className="lf-section-sub">
          From AI-curated slugs to retargeting pixels — LinkFlow is the complete toolkit for marketers, founders and growth teams.
        </p>

        <div className="lf-bento">
          <div className="lf-bento-card lf-large lf-dark">
            <div className="lf-feat-icon">
              <SparkleIcon size={20} />
            </div>
            <h3 className="lf-feat-title">AI slug suggestions, in milliseconds.</h3>
            <p className="lf-feat-desc">
              Claude reads the destination URL, understands the campaign context, and suggests memorable on-brand slugs. Stop arguing about naming in Slack.
            </p>
            <div className="lf-slug-demo">
              <div className="lf-slug-input">
                anthropic.com/news/claude-4-7-launch-...
              </div>
              <div className="lf-slug-suggestions">
                <div className="lf-slug-suggestion">
                  lfo.to/claude-4-7
                  <span className="lf-slug-meta">99% match</span>
                </div>
                <div className="lf-slug-suggestion lf-picked">
                  lfo.to/opus-launch
                  <span className="lf-slug-meta">Picked</span>
                </div>
                <div className="lf-slug-suggestion">
                  lfo.to/c47
                  <span className="lf-slug-meta">87% match</span>
                </div>
              </div>
            </div>
          </div>

          <div className="lf-bento-card lf-tall">
            <div className="lf-feat-icon lf-olive">
              <ChartIcon size={20} />
            </div>
            <h3 className="lf-feat-title">Realtime analytics.</h3>
            <p className="lf-feat-desc">
              Device, browser, referrer, geography — far beyond basic click counts. See it the moment it happens.
            </p>
            <div className="lf-mini-chart">
              <div className="lf-mini-bars">
                {[30, 55, 42, 70, 65, 85].map((h, i) => (
                  <div
                    key={i}
                    ref={registerBar}
                    className="lf-mini-bar"
                    style={{ height: `${h}%` }}
                  />
                ))}
                <div
                  ref={registerBar}
                  className="lf-mini-bar lf-coral"
                  style={{ height: '92%' }}
                />
              </div>
            </div>
          </div>

          <div className="lf-bento-card lf-wide">
            <div className="lf-feat-icon lf-mustard">
              <TargetIcon size={20} />
            </div>
            <h3 className="lf-feat-title">Retargeting pixels, drop-in.</h3>
            <p className="lf-feat-desc">
              Attach Meta, Google, LinkedIn or X pixels to any link. Build retargeting audiences from every click — no engineer needed.
            </p>
          </div>

          <div className="lf-bento-card lf-medium">
            <div className="lf-feat-icon">
              <QRBrandedIcon size={20} />
            </div>
            <h3 className="lf-feat-title">Branded QR codes.</h3>
            <p className="lf-feat-desc">
              Custom colors, logos, scan tracking. PNG or SVG.
            </p>
          </div>

          <div className="lf-bento-card lf-wide">
            <div className="lf-feat-icon lf-sage">
              <BoltIcon size={20} />
            </div>
            <h3 className="lf-feat-title">Sub-100ms redirects.</h3>
            <p className="lf-feat-desc">
              Edge-routed across 30+ regions. Your visitors don't wait. Median redirect: 28ms globally.
            </p>
          </div>

          <div className="lf-bento-card lf-medium">
            <div className="lf-feat-icon lf-olive">
              <ShieldIcon size={20} />
            </div>
            <h3 className="lf-feat-title">Privacy-first by default.</h3>
            <p className="lf-feat-desc">
              GDPR-native, cookieless tracking, no raw IPs stored.
            </p>
          </div>
        </div>
      </section>

      {/* ============================  HOW IT WORKS  ============================ */}
      <section className="lf-block" id="how">
        <div className="lf-section-label">How it works</div>
        <h2 className="lf-section-title">
          From URL to <em>insight</em> in three clicks.
        </h2>
        <p className="lf-section-sub">
          No setup. No SDKs. No engineering tickets. Paste, brand, ship.
        </p>

        <div className="lf-steps">
          <div className="lf-step">
            <span className="lf-step-num">01</span>
            <div className="lf-step-title">Paste a destination</div>
            <p className="lf-step-desc">
              Drop any URL. Claude fetches metadata, suggests slugs and tags automatically. Bulk-import 500 at a time.
            </p>
          </div>
          <div className="lf-step">
            <span className="lf-step-num">02</span>
            <div className="lf-step-title">Brand & route</div>
            <p className="lf-step-desc">
              Add UTMs, custom domains, A/B variants and retargeting pixels — with one click each.
            </p>
          </div>
          <div className="lf-step">
            <span className="lf-step-num">03</span>
            <div className="lf-step-title">Track everything</div>
            <p className="lf-step-desc">
              See live clicks, geos, devices and conversion lift in your realtime dashboard. Slack alerts when links go viral.
            </p>
          </div>
        </div>

        {/* ============================  TESTIMONIAL  ============================ */}
        <div className="lf-testimonial">
          <p className="lf-testimonial-quote">
            We replaced Bitly and a homegrown tracking script in one afternoon. The AI slugs alone save us an hour a week — and the realtime conversion data is what we'd been begging engineering for.
          </p>
          <div className="lf-testimonial-author">
            <div className="lf-author-avatar">MR</div>
            <div className="lf-author-info">
              <div className="lf-author-name">Maya Reinders</div>
              <div className="lf-author-role">Head of Growth · Northwave</div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================  PRICING  ============================ */}
      <section className="lf-block" id="pricing">
        <div style={{ textAlign: 'center' }}>
          <div className="lf-section-label">Pricing</div>
          <h2 className="lf-section-title" style={{ marginLeft: 'auto', marginRight: 'auto' }}>
            Start free. <em>Scale</em> when you're ready.
          </h2>
          <p className="lf-section-sub" style={{ marginLeft: 'auto', marginRight: 'auto' }}>
            Predictable, simple pricing. Upgrade anytime — no hidden fees, no per-click surprises.
          </p>
        </div>

        <div className="lf-pricing-grid">
          <div className="lf-price-card">
            <div className="lf-price-tier">Free</div>
            <div className="lf-price-amount">
              <span className="lf-price-num">$0</span>
              <span className="lf-price-per">/ forever</span>
            </div>
            <p className="lf-price-desc">For individuals getting started.</p>
            <ul className="lf-price-features">
              <li>Unlimited links</li>
              <li>5,000 tracked clicks/mo</li>
              <li>1 custom domain</li>
              <li>AI slug suggestions</li>
              <li>Folders, tags &amp; UTM presets</li>
              <li>Full REST API access</li>
            </ul>
            <button
              type="button"
              className="lf-btn lf-btn-outline lf-price-cta"
              onClick={handleGetStarted}
              data-testid="plan-free-btn"
            >
              Get started free
            </button>
          </div>

          <div className="lf-price-card lf-featured">
            <div className="lf-price-tag">Most popular</div>
            <div className="lf-price-tier">Pro</div>
            <div className="lf-price-amount">
              <span className="lf-price-num">$14</span>
              <span className="lf-price-per">/ month</span>
            </div>
            <p className="lf-price-desc">For growing teams &amp; marketers.</p>
            <ul className="lf-price-features">
              <li>Everything in Free</li>
              <li>Unlimited tracked clicks</li>
              <li>5 custom domains · 5 workspaces</li>
              <li>Bulk CSV import (500 links)</li>
              <li>Click threshold alerts</li>
              <li>A/B testing · CTA overlays · Zapier</li>
              <li>Retargeting pixels · advanced routing</li>
            </ul>
            <button
              type="button"
              className="lf-btn lf-btn-coral lf-price-cta"
              onClick={handleGetStarted}
              data-testid="plan-pro-btn"
            >
              Join Pro waitlist <span className="lf-arrow">→</span>
            </button>
          </div>

          <div className="lf-price-card">
            <div className="lf-price-tier">Business</div>
            <div className="lf-price-amount">
              <span className="lf-price-num">$49</span>
              <span className="lf-price-per">/ month</span>
            </div>
            <p className="lf-price-desc">For agencies &amp; enterprises.</p>
            <ul className="lf-price-features">
              <li>Everything in Pro</li>
              <li>50 custom domains · 50 workspaces</li>
              <li>25 team seats with RBAC roles</li>
              <li>Link health monitoring</li>
              <li>Conversion API (Meta CAPI + GA4)</li>
              <li>Audit log · SAML SSO</li>
              <li>Unlimited API · priority support</li>
            </ul>
            <a href="mailto:sales@linkflow.io" className="lf-btn lf-btn-outline lf-price-cta" data-testid="plan-business-btn">
              Contact sales
            </a>
          </div>
        </div>
      </section>

      {/* ============================  FINAL CTA  ============================ */}
      <div className="lf-final-cta">
        <div className="lf-final-cta-content">
          <div className="lf-final-label">Ready when you are</div>
          <h2 className="lf-final-title">
            Stop guessing.<br />
            Start <em>routing</em>.
          </h2>
          <p className="lf-final-sub">
            Join growth teams using LinkFlow to ship smarter campaigns, optimize CTR and retarget the right audiences.
          </p>
          <div className="lf-final-cta-row">
            <button type="button" className="lf-btn lf-btn-coral" onClick={handleGetStarted}>
              {user ? 'Open dashboard' : 'Start free'} <span className="lf-arrow">→</span>
            </button>
            <a href="#features" className="lf-btn lf-btn-outline">Book a 15-min demo</a>
          </div>
        </div>
      </div>

      {/* ============================  FOOTER  ============================ */}
      <footer className="lf-footer">
        <RouterLink to="/" className="lf-logo">
          <LogoMark />
          LinkFlow
        </RouterLink>
        <div>© {new Date().getFullYear()} LinkFlow — AI-native link management.</div>
      </footer>
    </div>
  );
}
